import classNames from 'classnames';
import uniqueId from 'lodash/uniqueId';
import * as React from 'react';
import type { ReactNode } from 'react';
import { useTheme } from '~/Features/Layouts/Theme/Context/ThemeContextProvider';
import type { HtmlString } from '~/Features/Types/HtmlString.csharp';
import { HtmlDiv } from '~/Partials/Controls/HtmlDiv';
import type { BaseImage } from '~/Partials/Picture/Picture';
import { Picture } from '~/Partials/Picture/Picture';
import { PictureProfiles } from '~/Partials/Picture/PictureProfiles';
import { PicturePlaceholder } from '~/Partials/Picture/Placeholder/PicturePlaceholder';
import { useRadioGroupContext } from '~/Partials/RadioGroup/RadioGroupContext';
import { useClickProxyAllowingTextSelection } from '~/Utils/Hooks/useClickProxyAllowingTextSelection';
import type { Theme } from '~/Utils/Theme';
import styles from './CampusCardCheckbox.module.css';

export interface CampusCardCheckboxProps {
    title: string;
    value: string;
    subtitle?: string | ReactNode;
    description?: HtmlString;
    image?: BaseImage;
    theme?: Pick<Theme, 'withRoundedCorners'>;
}

export const CampusCardCheckbox: React.FC<CampusCardCheckboxProps> = props => {
    const contextualTheme = useTheme();
    const { title, subtitle, description, value, image, theme = contextualTheme } = props;
    const { currentValue, onChange, name } = useRadioGroupContext();
    const checked = value === currentValue;

    const { withRoundedCorners } = theme;

    const { clickableRef, onProxyMouseDown, onProxyMouseUp } = useClickProxyAllowingTextSelection<HTMLInputElement>();

    const htmlId = uniqueId('FormComponents__radio__');

    return (
        <div
            className={classNames(
                styles.CampusCardCheckbox,
                checked && styles['Variant--selected'],
                withRoundedCorners && styles['Variant--rounded']
            )}
        >
            <div className={styles.Image}>
                {image ? (
                    <Picture {...image} profile={PictureProfiles.contentCard} />
                ) : (
                    <PicturePlaceholder theme={{ withRoundedCorners: false }} />
                )}
            </div>
            <label className={styles.Label} htmlFor={htmlId}>
                <span className={styles.LabelInner}>{title}</span>
            </label>
            <input
                ref={clickableRef}
                className={styles.Input}
                type="radio"
                id={htmlId}
                checked={checked}
                value={value}
                onChange={() => onChange(value)}
                name={name}
            />
            <div
                className={styles.Content}
                role="none"
                tabIndex={-1}
                onMouseDown={onProxyMouseDown}
                onMouseUp={onProxyMouseUp}
            >
                <div>
                    <h3 className={styles.Title}>{title}</h3>
                    {subtitle && <span className={styles.SubTitle}>{subtitle}</span>}
                </div>
                {description && (
                    <HtmlDiv
                        className={classNames(styles.Description, 'RichtextContent', `RichtextContent--dark`)}
                        html={description}
                    />
                )}
            </div>
        </div>
    );
};
